import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Toast, SpinLoading } from 'antd-mobile'
import { CheckRecord, CheckSummary, HazardLevel } from '@/types'
import { generateCheckSummary } from '@/services/generator'
import { saveCheckRecord } from '@/services/storage'
import { v4 as uuidv4 } from 'uuid'

function calcRiskLevel(items: { hazardLevel: HazardLevel }[]): 'high' | 'medium' | 'low' {
  const majorCount = items.filter(i => i.hazardLevel === HazardLevel.MAJOR).length
  if (majorCount >= 3) return 'high'
  if (majorCount > 0) return 'medium'
  return 'low'
}

export const SummaryPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState<CheckSummary | null>(null)
  const [recordId, setRecordId] = useState<string>('')

  const { checkType, basicInfo, selectedItems } = (location.state as {
    checkType: string
    basicInfo: {
      inspectedUnit: string
      inspectedAddress: string
      inspector: string
      stationManager?: string
      lastInspectionDate?: string
    }
    selectedItems: any[]
  }) || { checkType: '', basicInfo: {}, selectedItems: [] }

  useEffect(() => {
    if (!checkType || !basicInfo || selectedItems.length === 0) {
      Toast.show({ content: '数据不完整，请重新开始检查', icon: 'fail' })
      navigate('/')
      return
    }

    generateSummary()
  }, [])

  const generateSummary = async () => {
    setLoading(true)
    try {
      const record: CheckRecord = {
        id: uuidv4(),
        checkType: checkType as any,
        inspectedUnit: basicInfo.inspectedUnit,
        inspectedAddress: basicInfo.inspectedAddress,
        inspector: basicInfo.inspector,
        checkDate: new Date(),
        selectedItems: selectedItems,
        riskLevel: calcRiskLevel(selectedItems),
        createdAt: new Date(),
        updatedAt: new Date(),
        stationManager: basicInfo.stationManager || undefined,
        lastInspectionDate: basicInfo.lastInspectionDate || undefined,
      }

      const generatedSummary = await generateCheckSummary(record)
      setSummary(generatedSummary)
      setRecordId(record.id)
    } catch (error) {
      console.error('生成总结失败:', error)
      Toast.show({ content: '生成总结失败，请重试', icon: 'fail' })
    } finally {
      setLoading(false)
    }
  }

  const handleEditField = (field: keyof CheckSummary, value: string | string[]) => {
    if (!summary) return
    setSummary({ ...summary, [field]: value })
  }

  const handleNext = async () => {
    if (!summary) return

    setLoading(true)
    try {
      const record: CheckRecord = {
        id: recordId,
        checkType: checkType as any,
        inspectedUnit: basicInfo.inspectedUnit,
        inspectedAddress: basicInfo.inspectedAddress,
        inspector: basicInfo.inspector,
        checkDate: new Date(),
        selectedItems: selectedItems,
        summary: summary,
        riskLevel: calcRiskLevel(selectedItems),
        createdAt: new Date(),
        updatedAt: new Date(),
      }

      await saveCheckRecord(record)
      navigate('/report/' + recordId)
    } catch (error) {
      console.error('保存失败:', error)
      Toast.show({ content: '保存失败，请重试', icon: 'fail' })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <SpinLoading color="#c62828" />
        <p className="mt-4 text-gray-600">正在生成检查总结...</p>
      </div>
    )
  }

  if (!summary) return null

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 顶部标题栏 */}
      <div className="bg-red-700 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center">
          <button onClick={() => navigate(-1)} className="mr-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">检查总结</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* 检查概况 */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-base font-medium text-gray-800 mb-3">检查概况</h2>
          <textarea
            value={summary.overview}
            onChange={(e) => handleEditField('overview', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-red-500 focus:outline-none resize-none text-gray-700"
          />
        </div>

        {/* 重大隐患 */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-base font-medium text-red-600 mb-3">
            重大隐患（{summary.majorHazards.length}项）
          </h2>
          <div className="space-y-2">
            {summary.majorHazards.map((hazard, i) => (
              <div key={i} className="flex items-start">
                <span className="text-red-600 mr-2">{i + 1}.</span>
                <input
                  type="text"
                  value={hazard}
                  onChange={(e) => {
                    const newHazards = [...summary.majorHazards]
                    newHazards[i] = e.target.value
                    handleEditField('majorHazards', newHazards)
                  }}
                  className="flex-1 px-2 py-1 border-b border-gray-200 focus:border-red-500 focus:outline-none text-gray-700"
                />
              </div>
            ))}
          </div>
        </div>

        {/* 一般隐患 */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-base font-medium text-orange-500 mb-3">
            一般隐患（{summary.generalHazards.length}项）
          </h2>
          <div className="space-y-2">
            {summary.generalHazards.map((hazard, i) => (
              <div key={i} className="flex items-start">
                <span className="text-orange-500 mr-2">{i + 1}.</span>
                <input
                  type="text"
                  value={hazard}
                  onChange={(e) => {
                    const newHazards = [...summary.generalHazards]
                    newHazards[i] = e.target.value
                    handleEditField('generalHazards', newHazards)
                  }}
                  className="flex-1 px-2 py-1 border-b border-gray-200 focus:border-orange-500 focus:outline-none text-gray-700"
                />
              </div>
            ))}
          </div>
        </div>

        {/* 整改建议 */}
        <div className="bg-white rounded-lg p-4">
          <h2 className="text-base font-medium text-gray-800 mb-3">整改建议</h2>
          <div className="space-y-2">
            {summary.suggestions.map((suggestion, i) => (
              <div key={i} className="flex items-start">
                <span className="text-gray-500 mr-2">{i + 1}.</span>
                <input
                  type="text"
                  value={suggestion}
                  onChange={(e) => {
                    const newSuggestions = [...summary.suggestions]
                    newSuggestions[i] = e.target.value
                    handleEditField('suggestions', newSuggestions)
                  }}
                  className="flex-1 px-2 py-1 border-b border-gray-200 focus:border-red-500 focus:outline-none text-gray-700"
                />
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* 底部操作栏 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3">
        <button
          onClick={handleNext}
          className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700"
        >
          生成报告
        </button>
      </div>
    </div>
  )
}
